import * as Sentry from "@sentry/vue";

import type { backendOld } from "~/api";
import { isDefined } from "~/shared/common";
import type { Attachment, AttachmentCreate, AttachmentUpdate } from "~/shared/types";
import { truncateFilename } from "~/utils/api";
import { makeRandomColorHex } from "~/utils/color";
import { makeDuid } from "~/utils/common";
import { attachmentComparator } from "~/utils/comparator";
import { finishFileUpload, makeFileUploadProgressTracker, startFileUpload } from "~/utils/fileUpload";

import type { PiniaActionAdaptor, PiniaGetterAdaptor } from "../shared";
import type { DataStore } from ".";

const getPaginationKey = (taskDuid: string) => `attachments:${taskDuid}`;

// spell-checker:disable
const MIME_TYPES = new Map<string, string>([
  // Text & Code
  ["txt", "text/plain"],
  ["md", "text/markdown"],
  ["html", "text/html"],
  ["css", "text/css"],
  ["js", "application/javascript"],
  ["ts", "application/typescript"],
  ["json", "application/json"],
  ["xml", "application/xml"],
  ["csv", "text/csv"],
  // Images
  ["jpg", "image/jpeg"],
  ["jpeg", "image/jpeg"],
  ["png", "image/png"],
  ["gif", "image/gif"],
  ["webp", "image/webp"],
  ["svg", "image/svg+xml"],
  ["ico", "image/x-icon"],

  // Audio
  ["mp3", "audio/mpeg"],
  ["wav", "audio/wav"],
  ["ogg", "audio/ogg"],
  ["m4a", "audio/mp4"],
  // Video
  ["mp4", "video/mp4"],
  ["mov", "video/quicktime"],
  ["webm", "video/webm"],
  ["avi", "video/x-msvideo"],
  ["mkv", "video/x-matroska"],
  // Docs
  ["pdf", "application/pdf"],
  ["doc", "application/msword"],
  ["docx", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"],
  ["xls", "application/vnd.ms-excel"],
  ["xlsx", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"],
  ["ppt", "application/vnd.ms-powerpoint"],
  ["pptx", "application/vnd.openxmlformats-officedocument.presentationml.presentation"],
  // Archives
  ["zip", "application/zip"],
  ["tar", "application/x-tar"],
  ["rar", "application/vnd.rar"],
  ["7z", "application/x-7z-compressed"],
  ["gz", "application/gzip"],
  // Code (extended)
  ["py", "text/x-python"],
  ["java", "text/x-java-source"],
  ["c", "text/x-c"],
  ["cpp", "text/x-c++"],
  ["sh", "application/x-sh"],
  ["yml", "text/yaml"],
  ["yaml", "text/yaml"],
]);
// spell-checker:enable

type CreateAttachmentOptions = {
  taskDuid?: string;
  formDuid?: string;
  noBackend?: boolean;
  awaitBackend?: boolean;
};

export type State = {
  _duidsToAttachments: Map<string, Attachment>;
};

export const state: State = {
  _duidsToAttachments: new Map(),
};

export type Getters = {
  getAttachmentByDuid: (duid: string) => Promise<Attachment | undefined>;
  getAttachmentByDuidIfLoaded: (duid: string, options?: { assertLoaded?: boolean }) => Attachment | undefined;
  getAttachmentsByDuidsOrdered: (duids: string[]) => Promise<Attachment[]>;
  getAttachmentsByDuidsOrderedIfLoaded: (duids: string[]) => Attachment[];
  getLoadedAttachmentsByTaskDuidOrdered: (taskDuid: string) => Attachment[];
};

export type Actions = {
  loadAttachments: (
    ...args: Parameters<typeof backendOld.entityList.attachment>
  ) => ReturnType<typeof backendOld.entityList.attachment>;
  loadPaginatedAttachmentsForTaskDuid: (taskDuid: string) => Promise<number>;
  createAttachment: (
    file: File,
    order: string,
    partialAttachment?: Partial<Attachment>,
    options?: CreateAttachmentOptions
  ) => Promise<{ attachment: Attachment; create: AttachmentCreate }>;
  createAttachments: (
    attachmentConfigs: {
      file: File;
      order: string;
      partialAttachment?: Partial<Attachment>;
    }[],
    options?: CreateAttachmentOptions
  ) => Promise<{ attachment: Attachment; create: AttachmentCreate }[]>;
  updateAttachment: (update: AttachmentUpdate, options?: { awaitBackend?: boolean }) => Promise<Attachment | undefined>;
  deleteAttachment: (attachment: Attachment, options?: { awaitBackend?: boolean }) => Promise<void>;
  $createOrUpdateAttachment: (attachment: Attachment) => void;
  $deleteAttachment: (attachment: Attachment) => void;
};

const getters: PiniaGetterAdaptor<Getters, DataStore> = {
  getAttachmentByDuid() {
    return async (duid) => {
      const attachment = this.getAttachmentByDuidIfLoaded(duid);
      if (attachment) {
        return attachment;
      }

      if (this.$isEntityDuidBlacklisted(duid)) {
        return undefined;
      }

      const attachmentFetched = await this.$batchers.entityListManager.getEntityByDuid("attachments", duid);
      if (!attachmentFetched) {
        this.$blacklistEntityDuid("attachments", duid);
        return undefined;
      }

      this.$createOrUpdateAttachment(attachmentFetched);
      return attachmentFetched;
    };
  },
  getAttachmentByDuidIfLoaded() {
    return (duid, options = {}) => {
      const { assertLoaded = false } = options;
      const attachment = this._duidsToAttachments.get(duid);
      if (assertLoaded && !attachment) {
        Sentry.captureMessage(`Assert loaded attachment with duid not found: ${duid}`);
      }
      return attachment;
    };
  },
  getAttachmentsByDuidsOrdered() {
    return async (duids) => {
      const attachments = await Promise.all(duids.map((e) => this.getAttachmentByDuid(e)));
      return attachments.filter(isDefined).sort(attachmentComparator);
    };
  },
  getAttachmentsByDuidsOrderedIfLoaded() {
    return (duids) => {
      const attachments = duids.map((e) => this.getAttachmentByDuidIfLoaded(e));
      return attachments.filter(isDefined).sort(attachmentComparator);
    };
  },
  getLoadedAttachmentsByTaskDuidOrdered() {
    return (taskDuid) => {
      const paginationState = this.$getPaginationState(getPaginationKey(taskDuid), null);

      if (!paginationState) {
        return [];
      }

      const attachments = [...paginationState.duids.values()]
        .map((e) => this.getAttachmentByDuidIfLoaded(e))
        .filter(isDefined);
      return attachments;
    };
  },
};

const actions: PiniaActionAdaptor<Actions, DataStore> = {
  async loadAttachments(...args) {
    const response = await this.$backendOld.entityList.attachment(...args);
    response.data.results.forEach((attachment: Attachment) => {
      if (this.getAttachmentByDuidIfLoaded(attachment.duid)) {
        return;
      }
      this.$createOrUpdateAttachment(attachment);
    });
    return response;
  },
  async loadPaginatedAttachmentsForTaskDuid(taskDuid) {
    const task = await this.getTaskByDuid(taskDuid);
    if (!task) {
      return 0;
    }

    // If the task has no attachments, we don't need to load any
    if (task.attachmentDuids.length === 0) {
      this.$createPaginationState(getPaginationKey(taskDuid), null, attachmentComparator);
      return 0;
    }

    let paginationState = this.$getPaginationState(getPaginationKey(taskDuid), null);
    const { data } = await this.loadAttachments(taskDuid, paginationState?.duids.size ?? 0);
    if (!paginationState) {
      paginationState = this.$createPaginationState(getPaginationKey(taskDuid), null, attachmentComparator);
    }
    this.$addEntitiesToPaginationState(getPaginationKey(taskDuid), null, data.results, data.count);

    return data.count;
  },
  async createAttachment(file, order, partialAttachment, options = {}) {
    const { taskDuid, formDuid, noBackend = false, awaitBackend = false } = options;
    const attachmentDuid = partialAttachment?.duid ?? makeDuid();
    startFileUpload(attachmentDuid, file);
    const { data } = await this.$backendOld.createPresignedUrl("attachment", file.name, attachmentDuid, formDuid);

    const extension = file.name.split(".").pop() ?? "";
    const mimeType = (file.type || MIME_TYPES.get(extension)) ?? "application/octet-stream";
    const attachment: Attachment = {
      duid: attachmentDuid,
      name: truncateFilename(file.name, 150),
      kind: mimeType,
      order,
      fileUrl: data.fileUrl,
      colorHex: makeRandomColorHex(),
      recommendationDuid: null,
      ...partialAttachment,
    };
    const attachmentCreates: AttachmentCreate[] = [{ ...attachment, filePath: data.filePath }];

    if (taskDuid) {
      this.$addEntitiesToPaginationStates(getPaginationKey(taskDuid), [attachment]);
    }

    if (!noBackend) {
      const backendAction = this.$backend.attachment.createMany(attachmentCreates);
      if (awaitBackend) {
        await backendAction;
      }
    }

    // TODO refactor to remove this timeout--can do this by returning the code that happens in the timeout as a callback and calling that after the attachment is created
    // the reason that this is this way right now is that we need this code to run after the actual attachment create and setTimeout basically puts it on the next tick
    // and we we were too lazy to refactor it right now
    // eslint-disable-next-line no-restricted-syntax
    setTimeout(async () => {
      await this.$backendOld._put(
        data.presignedUrl,
        file,
        { authOverride: true, absolute: true, nonBlocking: true },
        data.signedHeaders,
        makeFileUploadProgressTracker(attachmentDuid)
      );
      finishFileUpload(attachmentDuid);
    });

    return { attachment, create: attachmentCreates[0] };
  },
  async createAttachments(attachmentConfigs, options) {
    return Promise.all(
      attachmentConfigs.map(async ({ file, order, partialAttachment }) =>
        this.createAttachment(file, order, partialAttachment, options)
      )
    );
  },
  async updateAttachment(update, options = {}) {
    const { awaitBackend = false } = options;
    const attachment = this.getAttachmentByDuidIfLoaded(update.duid);
    if (!attachment) {
      return undefined;
    }

    Object.assign(attachment, update);

    const backendAction = this.$backend.attachment.update(update);
    if (awaitBackend) {
      await backendAction;
    }
    return attachment;
  },
  async deleteAttachment(attachment, options = {}) {
    const { awaitBackend = false } = options;

    this.$deleteAttachment(attachment);

    this.$addEntityDuidPendingDeletion(attachment.duid);
    const backendAction = this.$backend.attachment.delete(attachment.duid);
    const action = (async () => {
      await backendAction;
      this.$removeEntityDuidPendingDeletion(attachment.duid);
    })();

    if (awaitBackend) {
      await action;
    }
  },
  $createOrUpdateAttachment(attachment) {
    const currentAttachment = this.getAttachmentByDuidIfLoaded(attachment.duid);
    if (this.$isEntityDuidPendingDeletion(attachment.duid)) {
      return;
    }

    if (!currentAttachment) {
      this._duidsToAttachments.set(attachment.duid, attachment);
    } else {
      Object.assign(currentAttachment, attachment);
    }

    this.$unblacklistEntityDuid(attachment.duid);
  },
  $deleteAttachment(attachment) {
    this._duidsToAttachments.delete(attachment.duid);
    this.$removeEntitiesFromPaginationStates([attachment]);
  },
};

export { actions, getters };
